import type {
  ScrollControllerApi,
  UseScrollControllerOptions,
} from "./scroll-controller-types";
import type { ScrollActions } from "./use-scroll-actions";
import { useScrollActions } from "./use-scroll-actions";
import {
  useScrollControllerRefs,
  type ScrollControllerRefs,
} from "./use-scroll-controller-refs";
import { useScrollControllerEffects } from "./use-scroll-controller-effects";
import {
  useScrollElementRegistration,
  type ScrollElementRegistration,
} from "./use-scroll-element-registration";
import {
  useScrollNearBottomState,
  type ScrollNearBottomState,
} from "./use-scroll-near-bottom-state";
import {
  useScrollVisibleMessageGetters,
  type ScrollVisibleMessageGetters,
} from "./use-scroll-visible-message-getters";

export type {
  ScrollBottomOptions,
  ScrollControllerApi,
  ScrollControllerContext,
  ScrollTargetOptions,
  UseScrollControllerOptions,
} from "./scroll-controller-types";

export function useScrollController({
  scrollRef,
  context,
  smoothScroll = true,
}: UseScrollControllerOptions): ScrollControllerApi {
  const refs = useScrollControllerRefs();
  const nearBottom = useScrollNearBottomState({ scrollRef });
  const actions = useScrollActions({
    scrollRef,
    smoothScroll,
    refs,
    setShowJumpToLatest: nearBottom.setShowJumpToLatest,
  });
  const visibleMessages = useScrollVisibleMessageGetters({
    scrollRef,
    refs,
  });

  useScrollControllerEffects({
    context,
    scrollRef,
    refs,
    nearBottom,
    actions,
  });

  const registration = useScrollElementRegistration({ refs, actions });

  return buildScrollControllerApi({
    refs,
    nearBottom,
    actions,
    visibleMessages,
    registration,
  });
}

function buildScrollControllerApi({
  refs,
  nearBottom,
  actions,
  visibleMessages,
  registration,
}: {
  refs: ScrollControllerRefs;
  nearBottom: ScrollNearBottomState;
  actions: ScrollActions;
  visibleMessages: ScrollVisibleMessageGetters;
  registration: ScrollElementRegistration;
}): ScrollControllerApi {
  return {
    ...registration,
    getMessageEl: (messageId: string) =>
      refs.messageElementsRef.current.get(messageId) ?? null,
    scrollToMessageId: actions.scrollToMessageId,
    scrollToVersionId: actions.scrollToVersionId,
    scrollToBottom: actions.scrollToBottom,
    cancelAutoScroll: actions.cancelAutoScroll,
    isNearBottom: nearBottom.isNearBottom,
    showJumpToLatest: nearBottom.showJumpToLatest,
    setShowJumpToLatest: nearBottom.setShowJumpToLatest,
    ...visibleMessages,
  };
}
